import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { Button } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { fetchSubscription } from '@/features/billing/api/billing';
import { LOCKED_SUBSCRIBE_PATH } from '@/features/billing/lib/billingLock';
import { SUBSCRIBE_PATH } from '@/features/billing/lib/planSelection';
import { isSettled, panelTrialRunning } from '@/features/billing/lib/subscriptionState';
import { useImpersonation } from '@/features/platform/hooks/useImpersonation';

/**
 * Dashboard paths a merchant without a settled plan may still open.
 *
 * Only billing: it is where a one-off payment the subscribe screen handed on is
 * watched until its webhook lands, and where a half-finished checkout blocking
 * every later attempt with `PAYMENT_IN_PROGRESS` is cancelled. Walling it off
 * would strand exactly the merchants who are in the middle of paying.
 */
const OPEN_WHILE_UNSETTLED = ['/app/billing'];

/**
 * Route guard: no dashboard until the merchant has settled a plan.
 *
 * Sign-up ends on `/subscribe`, and a merchant who closes that screen must be
 * sent back to it - on the next sign-in, on a refresh, and on typing any `/app`
 * address by hand - until they have chosen a plan the server has recorded.
 *
 * The answer is always the API's. The same `['subscription']` read the billing
 * page and the header use, refetched every time the dashboard mounts, so
 * nothing this browser remembers (a cached summary, a plan in local storage,
 * router state from checkout) can let somebody in. A cached "settled" is
 * trusted while the refetch runs, so a paying merchant is never held on a
 * spinner; a cached "unsettled" is not, because it is exactly what the cache
 * holds for a moment after checkout finishes and hands over to `/app`.
 *
 * A merchant on the free dashboard trial is let in while it runs. The guard
 * looks at the clock once a minute, so a tab left open is sent to choose a
 * plan when the trial ends rather than on the next navigation.
 *
 * Only a merchant who can act on the answer is asked. Staff on a support
 * session are there to look, and a member without `BILLING_READ` can neither
 * read the plan nor buy one - sending either to checkout would be a dead end.
 * Like every guard here this is UX, not security: the API authorises each
 * request on its own.
 */
export function RequireSubscription() {
  const { hasBusiness, isPlatformStaff, isPartner, billingLock, can } = useAuth();
  const impersonating = useImpersonation() !== null;
  const { pathname } = useLocation();

  const planMatters =
    hasBusiness && !isPlatformStaff && !isPartner && !impersonating && can('BILLING_READ');

  const subscription = useQuery({
    queryKey: ['subscription'],
    queryFn: fetchSubscription,
    enabled: planMatters,
    refetchOnMount: 'always',
  });

  const { data } = subscription;
  const trialRunning = data !== undefined && panelTrialRunning(data);
  // Re-render once a minute while the trial runs; the check below reads the
  // clock itself, so the trial closes on the first tick after it ends.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!trialRunning) return undefined;
    const timer = window.setInterval(() => setTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(timer);
  }, [trialRunning]);

  if (!planMatters) return <Outlet />;

  if (data !== undefined && isSettled(data)) return <Outlet />;

  if (subscription.isError && data === undefined && !subscription.isFetching) {
    return (
      <div className="grid min-h-dvh place-items-center px-5 text-center">
        <div>
          <p className="text-sm text-muted">We could not check your plan just now.</p>
          <Button className="mt-4" onClick={() => void subscription.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (data === undefined || subscription.isFetching) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <p className="text-sm text-muted">Loading your workspace…</p>
      </div>
    );
  }

  if (OPEN_WHILE_UNSETTLED.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
    return <Outlet />;
  }

  // A locked merchant is normally caught by `ProtectedRoute` above this
  // guard; answering the same way here means the two can never bounce a
  // merchant between `/subscribe` with the banner and `/subscribe` without.
  return <Navigate to={billingLock ? LOCKED_SUBSCRIBE_PATH : SUBSCRIBE_PATH} replace />;
}
